import { crmSystem } from "@/constants/crm/_shared/systems";
import { RefinedDataReturn } from "@/lib/useQuery/useGetExportData";
import { Autocomplete } from "@/types/utils";
import { CrmRegistryConstructorProps, CrmSystemListItem } from ".";
import { FallbackConfigurationParametersInput } from "../../crm/_common/ConfigurationParameterInput";
import { NotFoundForm } from "../../crm/_common/FieldForm";
import { NotFoundRenderer } from "../../crm/_common/FieldRenderer";
import { airtableCrmSystem } from "../../crm/airtable/crmSystem";
import { collaborateCrmSystem } from "../../crm/collaborate/crmSystem";
import { salesforceCrmSystem } from "../../crm/salesforce/crmSystem";
import { CrmBranding, CrmExportHandlerProps, CrmFieldTypeMap } from "../crmSystem";
import { CrmSystem } from "../crmSystem/crmSystem";
import { QuestionFormSchemaType } from "@/features/question/components/organisms/QuestionForm";
import { UseFormReturn } from "react-hook-form";
import { AllFieldTypes, CrmSystemIds } from "../crmSystem/crmSystem.types.gen";
import { Tables } from "@/lib/supabase/supabase_types_override";
import { DefineParametersStep, DefineParametersStepTitle } from "../../crm/_common/AddCrmModalLimited/DefineParametersStep";

class CrmRegistry {
    crmSystems: Map<string, CrmSystem<crmSystem>> = new Map();
    public constructor({ systems }: CrmRegistryConstructorProps) {
        systems.forEach(system => this.registerSystem(system));
    }
    private registerSystem(system: CrmSystem<crmSystem>): void {
        if (this.crmSystems.has(system.id)) {
            throw new Error(`CRM System with id ${system.id} is already registered.`);
        }
        this.crmSystems.set(system.id, system);
    }
    public getSystemById(crmSystem: Autocomplete<crmSystem> | undefined | null): CrmSystem<crmSystem> | undefined {
        if (!crmSystem) {
            return undefined;
        }
        return this.crmSystems.get(crmSystem);
    }
    public getSystemsList() {
        const res: Array<CrmSystemListItem> = [];
        this.crmSystems.forEach((system) => {
            res.push({
                id: system.id,
                name: system.name,
                branding: system.branding
            });
        });
        return res
    }
    public getOrgSystemsList<O extends Tables<'organisation'>>(org: O) {
        return this.getSystemsList().filter(system => org.crm_connections?.includes(system.id));
    }
    public getFieldFormField<CS extends crmSystem>(crmSystem: CS, fieldType: Autocomplete<CrmFieldTypeMap[CS]>): React.FC {
        const system = this.getSystemById(crmSystem);
        if (!system) {
            return NotFoundForm;
        }
        return system.getFieldFormField(fieldType) || NotFoundForm;
    }
    public getFieldRendererField<CS extends crmSystem>(crmSystem: CS, fieldType: Autocomplete<CrmFieldTypeMap[CS]>): React.FC {
        const system = this.getSystemById(crmSystem);
        if (!system) {
            return NotFoundRenderer;
        }
        return system.getFieldRendererField(fieldType) || NotFoundRenderer;
    }
    public getFieldsList<CS extends crmSystem>(crmSystem: CS): Array<{ id: string, name: string }> {
        const system = this.getSystemById(crmSystem);
        if (!system) {
            return [];
        }
        return system.getFieldsList();
    }
    public getFieldById<CS extends crmSystem>(crmSystem: CS | null, fieldId: string) {
        const system = this.getSystemById(crmSystem);
        if (!system) {
            return undefined;
        }
        return system.getFieldById(fieldId);
    }
    public getConfigurationParameterInput<CS extends crmSystem>(crmSystem: CS | undefined | null) {
        const system = this.getSystemById(crmSystem);
        if (!system) {
            return FallbackConfigurationParametersInput;
        }
        if (!system.configurationParameterInput) {
            return FallbackConfigurationParametersInput;
        }
        return system.configurationParameterInput;
    }

    public async parseExportData<CS extends crmSystem>(
        crmSystem: CS,
        data: RefinedDataReturn
    ): Promise<RefinedDataReturn | undefined> {
        return this.getSystemById(crmSystem)?.parser?.(data);
    }

    public parseFieldTypeToOtherCrmSystem<CS extends crmSystem>(fromCrmSystem: CS, toCrmSystem: CS, fieldType: Autocomplete<CrmFieldTypeMap[CS]>) {
        const fromSystem = this.getSystemById(fromCrmSystem);
        const toSystem = this.getSystemById(toCrmSystem);
        if (!fromSystem || !toSystem) {
            return undefined;
        }
        const fromField = fromSystem.fieldTypes[fieldType as CrmFieldTypeMap[CS]];
        if (!fromField) {
            return undefined;
        }
        return fromField.otherCrmSystemEquivalent[toSystem.id];
    }
    public getExportHandler<CS extends crmSystem>(crmSystem: CS): (props: CrmExportHandlerProps) => void {
        const system = this.getSystemById(crmSystem);
        if (!system) {
            throw new Error(`CRM System with id ${crmSystem} is not registered.`);
        }
        return system.exportHandler;
    }

    public getBranding<CS extends crmSystem>(crmSystem: CS): CrmBranding | undefined {
        return this.getSystemById(crmSystem)?.getBranding()
    }

    public resetFieldForm(crmSystem: crmSystem, field: AllFieldTypes, form: UseFormReturn<QuestionFormSchemaType>) {
        const system = this.getSystemById(crmSystem);
        if (!system) {
            throw new Error(`CRM System with id ${crmSystem} is not registered.`);
        }
        if(!field){
            return false;
        }
        const fieldType = system.fieldTypes[field];
        if (!fieldType) {
            throw new Error(`Field type ${field} is not supported by CRM System ${crmSystem}.`);
        }
        if (!system.resetForm(field, form)) {
            switch (fieldType.id) {
                case "text":
                case "textarea": {
                    form?.setValue("field_min", 0);
                    form?.setValue("field_max", null);
                    break;
                }
                case "checkbox":
                case "radio":
                case "picklist":
                case "picklist_multiple": {
                    form?.setValue("field_options", []);
                    break;
                }
                case "boolean": {
                    form?.setValue("field_min_label", "No");
                    form?.setValue("field_max_label", "Yes");
                    break;
                }
                case "date": {
                    form?.setValue("field_date_options.year_only", false);
                    break;
                }
                case "number": {
                    form?.setValue("field_min", 0);
                    form?.setValue("field_max", 10);
                    form?.setValue("field_integer_places", 2);
                    form?.setValue("field_decimal_places", 2);
                    break;
                }
                case "currency": {
                    form?.setValue("field_min", 0);
                    form?.setValue("field_max", null);
                    form?.setValue("field_integer_places", null);
                    form?.setValue("field_decimal_places", 2);
                    break;
                }
                case "slider": {
                    form?.setValue("field_min", 0);
                    form?.setValue("field_max", 10);
                    form?.setValue("field_step", 1);
                    form?.setValue("field_min_label", "");
                    form?.setValue("field_max_label", "");
                    form?.setValue("field_label", "");
                    form?.setValue("field_default_value", 5);
                    break;
                }
            }
        } else {
            system.resetForm(field, form);
        }
    }
    addCrmModalLimitedStepsResolver(crmSystem: Autocomplete<CrmSystemIds> | undefined | null) {
        const system = this.getSystemById(crmSystem);
        const defaultSteps = [
                {
                    stepTitle: DefineParametersStepTitle,
                    stepComponent: DefineParametersStep
                }
            ];
        if (!system?.addCrmModalLimitedSteps) {
            return defaultSteps;
        }
        return system.addCrmModalLimitedSteps;
    }
}
// Singleton export
export const crmRegistry = new CrmRegistry({
    systems: [
        collaborateCrmSystem,
        airtableCrmSystem,
        salesforceCrmSystem,
    ]
});